import { add } from 'date-fns';
import { getSessionUUID } from '../components/lib/AuthToken';
import logger from "../utils/logger";


/**
 * Orders API functions
 * Frontend API layer for warehouse orders management
 */

// Get all products with their current stock levels
export const getProductsWithStock = async (customerId = null) => {
    try {
        const sessionToken = await getSessionUUID();

        const url = customerId 
            ? `/enhanced_warehouse/products_with_stock?customer_id=${customerId}`
            : '/enhanced_warehouse/products_with_stock';

        const requestOptions = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            }
        };

        const response = await fetch(url, requestOptions);
        const responseBody = await response.json();

        return {
            message: responseBody.error || 'Request successful',
            status: response.status,
            data: responseBody || null,
            success: response.ok
        };
    } catch (err) {
        logger.error("Error fetching products with stock:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
            success: false
        };
    }
};

// Get pallet locations for a specific product
export const getProductPalletLocations = async (productInfo, customerId) => {
    try {
        const sessionToken = await getSessionUUID();

        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
            body: JSON.stringify({
                product_info: productInfo,
                customer_id: parseInt(customerId)
            })
        };

        const response = await fetch('/api/orders/product-pallet-locations', requestOptions);
        const responseBody = await response.json();

        return {
            message: responseBody.error || 'Request successful',
            status: response.status,
            data: responseBody.data || null,
            success: response.ok
        };
    } catch (err) {
        logger.error("Error fetching product pallet locations:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
            success: false
        };
    }
};

// Create a new order
export const createOrder = async (orderData) => {
    try {
        const sessionToken = await getSessionUUID();

        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
            body: JSON.stringify(orderData)
        };

        const response = await fetch('/enhanced_warehouse/create_order', requestOptions);
        
        // Check if response is HTML (indicates session expiration/redirect)
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('text/html')) {
            throw new Error('Your session has expired. Please log in again.');
        }
        
        const responseBody = await response.json();

        return {
            message: responseBody.error || responseBody.message || 'Order created successfully',
            status: response.status,
            data: responseBody || null,
            success: response.ok
        };
    } catch (err) {
        logger.error("Error creating order:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
            success: false
        };
    }
};

// Get all orders
export const getOrders = async (customerId = null) => {
    try {
        const sessionToken = await getSessionUUID();

        const url = customerId 
            ? `/enhanced_warehouse/orders?customer_id=${customerId}`
            : '/enhanced_warehouse/orders';

        const requestOptions = {
            method: 'GET',
            cache: 'no-store',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            }
        };

        const response = await fetch(url, requestOptions);
        const responseBody = await response.json();

        return {
            message: responseBody.error || 'Request successful',
            status: response.status,
            data: responseBody || null,
            success: response.ok
        };
    } catch (err) {
        logger.error("Error fetching orders:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
            success: false
        };
    }
};

// Get detailed order information
export const getOrderDetails = async (orderId) => {
    try {
        const sessionToken = await getSessionUUID();

        const requestOptions = {
            method: 'GET',
            cache: 'no-store',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            }
        };

        const response = await fetch(`/enhanced_warehouse/order/${orderId}`, requestOptions);
        const responseBody = await response.json();

        return {
            message: responseBody.error || 'Request successful',
            status: response.status,
            data: responseBody || null,
            success: response.ok
        };
    } catch (err) {
        logger.error("Error fetching order details:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
            success: false
        };
    }
};

// Update order status
export const updateOrderStatus = async (orderId, status, additionalCosts) => {
    try {
        const sessionToken = await getSessionUUID();

        const requestOptions = {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
            body: JSON.stringify({ status, additional_costs: additionalCosts })
        };

        const response = await fetch(`/enhanced_warehouse/order/${orderId}/status`, requestOptions);
        const responseBody = await response.json();

        return {
            message: responseBody.error || responseBody.message || 'Order status updated successfully',
            status: response.status,
            data: responseBody || null,
            success: response.ok
        };
    } catch (err) {
        logger.error("Error updating order status:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
            success: false
        };
    }
};

// Reset order status (admin only)
export const resetOrderStatus = async (orderId) => {
    try {
        const sessionToken = await getSessionUUID();

        const requestOptions = {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            }
        };

        const response = await fetch(`/enhanced_warehouse/order/${orderId}/reset-status`, requestOptions);
        const responseBody = await response.json();

        return {
            message: responseBody.error || responseBody.message || 'Order status reset successfully',
            status: response.status,
            data: responseBody || null,
            success: response.ok
        };
    } catch (err) {
        logger.error("Error resetting order status:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
            success: false
        };
    }
}

// Cancel/Delete an order
export const cancelOrder = async (orderId) => {
    try {
        const sessionToken = await getSessionUUID();

        const requestOptions = {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            }
        };

        const response = await fetch(`/enhanced_warehouse/order/${orderId}`, requestOptions);
        const responseBody = await response.json();

        return {
            message: responseBody.error || responseBody.message || 'Order cancelled successfully',
            status: response.status,
            data: responseBody || null,
            success: response.ok
        };
    } catch (err) {
        logger.error("Error cancelling order:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
            success: false
        };
    }
};

// Generate delivery note for a dispatched order
export const generateDeliveryNote = async (orderId) => {
    try {
        const sessionToken = await getSessionUUID();

        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            }
        };

        const response = await fetch(`/enhanced_warehouse/order/${orderId}/delivery-note`, requestOptions);

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to generate delivery note');
        }

        // Handle PDF download
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        
        // Extract filename from response headers or use default
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'delivery-note.pdf';
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (filenameMatch) {
                filename = filenameMatch[1].replace(/['"]/g, '');
            }
        }
        
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        return {
            message: 'Delivery note generated and downloaded successfully',
            status: response.status,
            success: true
        };
    } catch (err) {
        logger.error("Error generating delivery note:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
            success: false
        };
    }
};

// Download the dispatched order summary PDF
export const downloadOrderSummary = async (orderId) => {
    try {
        const sessionToken = await getSessionUUID();
        const response = await fetch(`/enhanced_warehouse/order/${orderId}/summary-pdf`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${sessionToken}` }
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to generate order summary');
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        const disposition = response.headers.get('Content-Disposition') || '';
        const filenameMatch = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        link.href = url;
        link.download = filenameMatch ? filenameMatch[1].replace(/['"]/g, '') : `OrderSummary-${orderId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        return { success: true };
    } catch (err) {
        logger.error('Error downloading order summary:', err);
        return { success: false, message: err.message || 'Unable to download order summary' };
    }
};

// Enhanced Picking API Functions

// Pick order with quantity tracking and discrepancy reasons
export const pickOrderWithQuantities = async (orderId, pickedItems, additionalCosts = null) => {
    try {
        const sessionToken = await getSessionUUID();

        const requestBody = {
            picked_items: pickedItems
        };

        // Add additional costs if provided
        if (additionalCosts) {
            requestBody.additional_costs = additionalCosts;
        }

        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
            body: JSON.stringify(requestBody)
        };

        const response = await fetch(`/enhanced_warehouse/order/${orderId}/pick`, requestOptions);
        const responseBody = await response.json();

        return {
            message: responseBody.message || responseBody.error || 'Order picked successfully',
            status: response.status,
            data: responseBody || null,
            success: response.ok
        };
    } catch (err) {
        logger.error("Error picking order with quantities:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
            success: false
        };
    }
};

// Get detailed picking status for an order
export const getOrderPickStatus = async (orderId) => {
    try {
        const sessionToken = await getSessionUUID();

        const requestOptions = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            }
        };

        const response = await fetch(`/enhanced_warehouse/order/${orderId}/pick-status`, requestOptions);
        const responseBody = await response.json();

        return {
            message: responseBody.error || 'Request successful',
            status: response.status,
            data: responseBody || null,
            success: response.ok
        };
    } catch (err) {
        logger.error("Error getting order pick status:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
            success: false
        };
    }
};

// Dispatch order using picked quantities for stock updates
export const dispatchOrderWithPickedQuantities = async (orderId, dispatchEvidence = { source: 'web' }) => {
    try {
        const sessionToken = await getSessionUUID();

        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
            body: JSON.stringify({ dispatch_evidence: dispatchEvidence })
        };

        const response = await fetch(`/enhanced_warehouse/order/${orderId}/dispatch`, requestOptions);
        const responseBody = await response.json();

        return {
            message: responseBody.message || responseBody.error || 'Order dispatched successfully',
            status: response.status,
            data: responseBody || null,
            success: response.ok
        };
    } catch (err) {
        logger.error("Error dispatching order with picked quantities:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
            success: false
        };
    }
};

// Get order details by order_number
export const getOrderDetailsByNumber = async (orderNumber) => {
    try {
        const sessionToken = await getSessionUUID();

        const requestOptions = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            }
        };

        const response = await fetch(`/enhanced_warehouse/order/by-number/${orderNumber}`, requestOptions);
        const responseBody = await response.json();

        return {
            message: responseBody.error || 'Request successful',
            status: response.status,
            data: responseBody || null,
            success: response.ok
        };
    } catch (err) {
        logger.error("Error fetching order details by number:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
            success: false
        };
    }
};

// Update order notes (admin only)
export const updateOrderNotes = async (orderId, notes) => {
    try {
        const sessionToken = await getSessionUUID();

        const requestOptions = {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
            body: JSON.stringify({ notes })
        };

        const response = await fetch(`/api/orders/order/${orderId}/notes`, requestOptions);
        const responseBody = await response.json();

        return {
            message: responseBody.error || responseBody.message || 'Order notes updated successfully',
            status: response.status,
            data: responseBody || null,
            success: response.ok
        };
    } catch (err) {
        logger.error("Error updating order notes:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
            success: false
        };
    }
};
